/**
 * Cross-viewport jump targets, expressed in DICOM world coordinates.
 *
 * Cornerstone3D's `viewport.jumpToWorld(ipp)` navigates any volume viewport
 * to the slice containing the given world point, regardless of how that
 * viewport's K-step axis happens to align with the underlying imageIds list
 * order. World space is universal across volumes of the same study, so a
 * single IPP can drive axial + sagittal + coronal viewports simultaneously
 * without per-volume index translation.
 *
 * This atom is the SSoT for "where the most recent AI-finding click wants
 * the user to be looking". Slot-level scroll state (`slotState.sliceIndex`)
 * remains list-index per slot, fed by the slider/scrollbar; the world atom
 * is a separate channel used only for cross-viewport jumps.
 */
import { atom } from "jotai";

export type Vec3 = readonly [number, number, number];

export interface AiJumpTarget {
  /** Patient-space coordinate of the target slice, from canonical
   *  metadata.json's `image_position_patient` for the AI's named instance. */
  ipp: Vec3;
  /** Series the AI minted the finding against. */
  seriesId: string;
  /** Backend list-index of the target instance within that series. */
  sliceIndex: number;
  /** FrameOfReferenceUID of the AI's series. Listening viewports MUST bail
   *  unless their own series shares this FoR — world coordinates are only
   *  comparable inside a single FoR. Null when the AI's series lacks an FoR
   *  in canonical metadata (degenerate / scout-only). */
  frameOfReferenceUid: string | null;
  centerWorld: Vec3;
  /** Whether panes draw the finding locator ring at `centerWorld`. False for a
   *  jump with no in-plane location (device panel key slices), where the ring
   *  would mark the slice origin rather than the finding. */
  locate: boolean;
  pixelCentroid?: readonly [number, number];
  segmentIndex?: number;
  /** Monotonic counter — bumped on every set so consumers re-fire even when
   *  the user clicks the same finding twice (the value would otherwise look
   *  unchanged to a useEffect dep). */
  trigger: number;
}

export const aiJumpTargetAtom = atom<AiJumpTarget | null>(null);
